import { workspaceHref } from "@/cowork/workspace-route"
import { collectionPath } from "@/cowork/scope-url"
import { useLanguage } from "@/context/language"
import type { AccountCardSelection } from "@/cowork/report-templates"
import { coworkAccountingReportTemplates } from "@/cowork/report-templates"
import { CoworkReportTemplateCatalog, type CoworkReportTemplate } from "@/components/cowork-report-template-catalog"
import { useServer } from "@/context/server"
import { useTabs } from "@/context/tabs"
import { createSession, getWorkspaceSettings, listWorkspaceAgents } from "@/cowork/api"
import { selectDefaultCoworkAgent } from "@/cowork/default-agent"
import { openCoworkChat } from "@/cowork/open-chat"
import { buildCoworkReportTemplatePrompt, coworkReportOutputLanguageInstructionValue } from "@/cowork/report-prompt"
import {
  acceptedReportTemplateScope,
  createReportTemplateLauncher,
  type ReportTemplateLaunchState,
} from "@/cowork/report-template-launch"
import { queueReportTemplate } from "@/cowork/report-template-request"
import { coworkResponseLocale } from "@/cowork/response-language"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { A, useLocation, useNavigate } from "@solidjs/router"
import { createComputed, createEffect, createMemo, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function CoworkReportTemplatesPage() {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const server = useServer()
  const tabs = useTabs()
  const location = useLocation()
  const navigate = useNavigate()
  let onboardingLaunchStarted = false
  const [launch, setLaunch] = createStore<ReportTemplateLaunchState>({
    busy: false,
    retry: false,
    templateID: "",
    title: "",
  })
  const scope = createMemo(() => acceptedReportTemplateScope(context))
  const launcher = createReportTemplateLauncher({
    createSession,
    getWorkspaceSettings,
    listWorkspaceAgents,
    selectDefaultAgent: selectDefaultCoworkAgent,
    onState: setLaunch,
    open: (target, request, isCurrent) =>
      openCoworkChat(target, {
        context,
        server,
        tabs,
        isCurrent,
        beforeOpen: (tab) => {
          queueReportTemplate(tabs, tab, request)
        },
      }),
  })
  // Observe transitions synchronously, not just equality after an async response (A -> B -> A).
  createComputed(() =>
    launcher.setScope(
      scope(),
      JSON.stringify([
        context.state.entityId,
        context.state.projectId,
        scope() ? scope()!.periodID : context.state.periodId,
        !!scope(),
      ]),
    ),
  )
  onCleanup(() => launcher.dispose())
  const generate = (template: CoworkReportTemplate, accountCard?: AccountCardSelection) => {
    const locale = coworkResponseLocale(language.locale(), scope()?.companyID)
    void launcher.generate({
      id: crypto.randomUUID(),
      templateID: template.id,
      title: language.t(template.titleKey),
      locale,
      ...buildCoworkReportTemplatePrompt(template, language.t, accountCard),
      reportOutputLanguage: coworkReportOutputLanguageInstructionValue(locale),
    })
  }

  createEffect(() => {
    if (onboardingLaunchStarted) return
    const params = new URLSearchParams(location.search)
    const companyID = params.get("onboardingCompany")
    const projectID = params.get("onboardingProject")
    const template = coworkAccountingReportTemplates.find(
      (item) => item.id === params.get("onboardingTemplate") && item.availability === "enabled",
    )
    if (!companyID || !projectID || !template) return
    if (context.state.entityId !== companyID) {
      context.selectEntity(companyID)
      return
    }
    if (context.projects.state !== "ready") return
    if (!context.projects()?.some((project) => project.id === projectID && project.company_id === companyID)) return
    if (context.state.projectId !== projectID) {
      context.selectProject(projectID)
      return
    }
    if (scope()?.projectID !== projectID || scope()?.companyID !== companyID) return
    onboardingLaunchStarted = true
    generate(template)
    navigate(workspaceHref(collectionPath("report-templates", {
      companyID, projectID, periodID: scope()?.periodID || undefined,
    })), { replace: true })
  })

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1500px]">
        <header>
          <A href={workspaceHref(collectionPath("reports", {
            companyID: context.state.entityId || undefined,
            projectID: context.state.projectId || undefined,
            periodID: scope()?.periodID || undefined,
          }))} class="mb-4 inline-block text-[12px] text-v2-text-text-muted hover:underline">
            {language.t("cowork.accountingNav.backToList")}
          </A>
          <h1 class="text-[30px] font-semibold tracking-[-0.025em]">
            {language.t("cowork.agent.settings.templates.title")}
          </h1>
          <p class="mt-1 text-[13px] text-v2-text-text-muted">
            {language.t("cowork.enterprise.reportTemplates.description")}
          </p>
          <p id="report-template-scope" class="mt-3 text-[12px] text-v2-text-text-muted">
            {scope()
              ? language.t("cowork.enterprise.reportTemplates.launch.scope", {
                  project: context.selectedProject()?.name ?? "",
                  period: scope()?.periodID
                    ? (context.selectedPeriod()?.name ?? "")
                    : language.t("cowork.enterprise.scope.allPeriods"),
                })
              : language.t(
                  context.selectedProject()
                    ? "cowork.enterprise.reportTemplates.launch.selectPeriod"
                    : "cowork.enterprise.reportTemplates.launch.selectProject",
                )}
          </p>
          <div role="status" aria-live="polite" class="mt-3 text-[12px] text-v2-text-text-muted">
            <Show when={launch.busy}>
              <p>{language.t("cowork.enterprise.reportTemplates.launch.starting", { template: launch.title })}</p>
            </Show>
            <Show when={launch.notice}>
              {(notice) => (
                <p>{language.t(`cowork.enterprise.reportTemplates.launch.${notice()}`, { title: launch.title })}</p>
              )}
            </Show>
            <Show when={launch.trialError}>
              {(key) => <p role="alert" class="text-v2-state-fg-danger">{language.t(key())}</p>}
            </Show>
            <Show when={launch.retry}>
              <button
                type="button"
                disabled={launch.busy}
                class="mt-2 rounded-md border border-v2-border-border-base px-3 py-2 disabled:opacity-50"
                onClick={() => void launcher.retry()}
              >
                {language.t("cowork.newChat.retry")}
              </button>
            </Show>
            <Show
              when={
                launch.notice === "checkChats" || launch.notice === "createdNotSent" || launch.notice === "openFailed"
              }
            >
              <A href={workspaceHref("/chats")} class="ml-3 underline">
                {language.t("cowork.enterprise.reportTemplates.launch.viewChats")}
              </A>
            </Show>
          </div>
        </header>

        <div class="mt-7">
          <CoworkReportTemplateCatalog
            disabled={launch.busy || !scope()}
            busyTemplateID={launch.busy ? launch.templateID : undefined}
            describedBy="report-template-scope"
            accountCardNeedsPeriod={!scope()?.periodID}
            onGenerate={generate}
          />
        </div>
      </div>
    </div>
  )
}
